import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, finalize } from 'rxjs/operators';
import { UserModel, TokenDecoded, TokenModel } from '../classes/user.model';
import { AppStorageService } from './storage.service';
import { StorageKey } from '@ciptech/classes/storage.model';
import { WsAuthService } from '../http/ws-auth.service';
import { environment } from 'apps/ciptech/src/environments/environment';


@Injectable()
export class SessionService {
	private _storage: TokenDecoded | null = null;
	private _userCache: UserModel | null = null;
	private _lastTokenValue: string | null = null;
	// private helper = new JwtHelperService();

	// Data de usuario
	userLogged$: Observable<UserModel | null>;

	// Seguimiento al logout
	private logoutSubject: Subject<void>;
	logout$: Observable<void>;

	// Control de sesión idle y refresh token
	private tokenWatcher: any;
	private tokenRefreshTime: number = 60000; // 1 min defecto

	private lastActivity = Date.now();
	private idleLimit = 15 * 60 * 1000;

	private isRefreshingToken = false;
	private refreshTokenWatcher: any;

	constructor(
		private authService: WsAuthService,
		private appStorage: AppStorageService
	) {
		// Refresh time
		let timeRefreshMin: number = environment.PREVIOS_TOKEN_REFRESH_MIN > 0 ? environment.PREVIOS_TOKEN_REFRESH_MIN * 60 * 1000 : 0;
		let timeRefreshSeg: number = environment.PREVIOS_TOKEN_REFRESH_SEG > 0 ? environment.PREVIOS_TOKEN_REFRESH_SEG * 1000 : 0;
		if (timeRefreshSeg == 0 && timeRefreshMin == 0)
			this.tokenRefreshTime = 60000;
		else
			this.tokenRefreshTime = timeRefreshMin + timeRefreshSeg;

		// Idle time
		let timeIdleMin: number = environment.IDLE_TIMEOUT_MIN > 0 ? environment.IDLE_TIMEOUT_MIN * 60 * 1000 : 0;
		let timeIdleSeg: number = environment.IDLE_TIMEOUT_SEG > 0 ? environment.IDLE_TIMEOUT_SEG * 1000 : 0;
		if (timeIdleSeg == 0 && timeIdleMin == 0)
			this.idleLimit = 15 * 60 * 1000; // 15 minutes default
		else
			this.idleLimit = timeIdleMin + timeIdleSeg;

		const initialToken = this.token;
		if (initialToken &&
			!this.appStorage.get<TokenDecoded>(StorageKey.Session)) {

			this.appStorage.set(
				StorageKey.Session,
				initialToken
			);
		}
		this.userLogged$ = this.appStorage
			.select<TokenDecoded>(StorageKey.Session)
			.pipe(
				map(token => this.validateTokenStorage(token)),
				map(token => token ? new UserModel(token) : null)
			);

		this.appStorage
			.select<TokenDecoded>(StorageKey.Session)
			.subscribe(token => {
				this._storage = null;
				this._userCache = null;
				this._lastTokenValue = null;

				const validatedToken = this.validateTokenStorage(token);
				if (!validatedToken) {
					clearTimeout(this.tokenWatcher);
					clearTimeout(this.refreshTokenWatcher);
					this.isRefreshingToken = false;
					return;
				}
			});
		this.logoutSubject = new Subject<void>();
		this.logout$ = this.logoutSubject.asObservable();
	}

	get user(): UserModel | null {
		const token = this.token;
		if (!token) {
			this._userCache = null;
			this._lastTokenValue = null;
			return null;
		}
		if (this._lastTokenValue !== token.access_token) {
			this._userCache = new UserModel(token);
			this._lastTokenValue = token.access_token;
		}

		return this._userCache;
	}

	get token(): TokenDecoded | null {
		try {
			const token = this.appStorage.get<TokenDecoded>(StorageKey.Session);
			const validatedToken = this.validateTokenStorage(token);
			this._storage = validatedToken;
			return validatedToken;
		} catch (e) {
			this.logout();
			return null;
		}
	}

	get isTokenExpired(): boolean {
		const token = this.token;
		if (!token) {
			return true;
		}
		return new TokenModel(token).isExpired;
	}

	private validateTokenStorage(data: TokenDecoded | null): TokenDecoded | null {
		try {
			if (!data) {
				return null;
			}
			return data;
		} catch (e) {
			return null;
		}
	}

	private startRefreshTokenWatcher(): void {
		clearTimeout(this.refreshTokenWatcher);
		const token = this.token;
		if (!token) {
			return;
		}

		const tokenModel = new TokenModel(token);
		if (tokenModel.isRefreshExpired) {
			this.logout();
			return;
		}

		const timeout = tokenModel.refreshExpirationDate.getTime() - Date.now();
		this.refreshTokenWatcher = setTimeout(() => {
			this.logout();
		}, Math.max(timeout, 0));
	}

	updateActivity() {
		this.lastActivity = Date.now();
	}

	private checkIdle(): boolean {
		const inactiveTime = Date.now() - this.lastActivity;
		var val = inactiveTime < this.idleLimit;
		return val;
	}

	initializeActivityListeners(): void {
		const events = ['click', 'touchstart'];
		events.forEach(event => {
			window.addEventListener(event, () => {
				this.updateActivity();
			});
		});
	}

	clearToken(): void {
		this._storage = null;
		this._userCache = null;
		this._lastTokenValue = null;
		this.appStorage.remove(StorageKey.Session);
	}

	private tryRefreshToken(): void {
		if (this.isRefreshingToken) {
			return;
		}
		this.isRefreshingToken = true;
		const refreshToken = this.token?.refresh_token;
		if (!refreshToken) {
			this.isRefreshingToken = false;
			this.logout();
			return;
		}

		this.authService.refreshToken(refreshToken)
			.pipe(
				finalize(() => {
					this.isRefreshingToken = false;
				})
			)
			.subscribe({
				next: token => {
					this.create(token);
				},
				error: () => {
					this.logout();
				}
			});
	}

	private startTokenWatcher(): void {
		clearTimeout(this.tokenWatcher);
		const token = this.token;
		if (!token) {
			return;
		}

		const tokenModel = new TokenModel(token);
		if (tokenModel.isExpired) {
			this.tryRefreshToken();
			return;
		}

		const timeout = tokenModel.expirationDate.getTime() - Date.now() - this.tokenRefreshTime;
		this.tokenWatcher = setTimeout(() => {
			if (this.checkIdle()) {
				this.tryRefreshToken();
			}
		}, Math.max(timeout, 0));
	}

	logout(): void {
		clearTimeout(this.tokenWatcher);
		clearTimeout(this.refreshTokenWatcher);
		this.isRefreshingToken = false;
		this.clearToken();
		this.destroy();
		this.logoutSubject.next();
	}

	create(token: string | TokenDecoded) {
		const tokenData = typeof token === 'string'
			? JSON.parse(token) as TokenDecoded
			: token;

		this.appStorage.set(StorageKey.Session, tokenData);
		this._storage = null;
		this.startTokenWatcher();
		this.startRefreshTokenWatcher();
	}

	restoreSessionWatcher(): void {
		const token = this.token;
		if (!token) {
			return;
		}

		const tokenModel = new TokenModel(token);
		if (tokenModel.isRefreshExpired) {
			this.logout();
			return;
		}

		this.updateActivity();
		this.startTokenWatcher();
		this.startRefreshTokenWatcher();
	}

	destroy() {
		this._storage = null;
		this.removeLocalStorageData();
	}

	private removeLocalStorageData() {
		this.appStorage.remove(StorageKey.Cart);
		this.appStorage.remove(StorageKey.Money);
		this.appStorage.remove(StorageKey.Session);
	}
}
